/**
 * ARABIC-INSENSITIVE MATCHING — the TypeScript half (bugs: G-06 / INV-F9,
 * "أنور" = "انور").
 *
 * Arabic has several spellings a reader treats as the same word: the hamza
 * carriers (أ إ آ ٱ) are all "an alef" when typed in a hurry, ة and ه swap at a
 * word's end ("مكتبة" / "مكتبه"), ى and ي swap at a word's end ("علي" / "على"),
 * and the vowel marks (tashkeel) are optional decoration a search box must not
 * be defeated by. A search that compares the raw code points refuses the exact
 * record the person is looking at.
 *
 * ONE RULE SET, TWO PLACES. The database twin is `ar_norm(text)` (migration
 * 20261012100000), used in every name `ILIKE` and behind the functional
 * indexes on partner and item names. The two MUST agree character for
 * character, or a row the server finds is one the client's local filter then
 * drops. Change one, change both, and the `ar-norm.spec.ts` DB test pins the
 * agreement on a fixed word list.
 *
 * It is here, in `@palaccount/config`, because it is the one package both the
 * API and the web app already depend on and that carries no runtime of its own.
 */
/** The hamza carriers and alef wasla, all read as a bare alef. */
const ALEF_FORMS = /[أإآٱ]/g;
/** Tashkeel (U+064B–U+0652), the superscript alef (U+0670) and the tatweel (U+0640). */
const STRIPPED = /[ً-ْٰـ]/g;
/** Arabic-Indic (U+0660–U+0669) and Extended Arabic-Indic (U+06F0–U+06F9) digits. */
const ARABIC_DIGITS = /[٠-٩۰-۹]/g;
const SINGLE_LETTERS = {
    'ة': 'ه', // ة → ه
    'ى': 'ي', // ى → ي
    'ؤ': 'و', // ؤ → و
    'ئ': 'ي', // ئ → ي
};
const SINGLE_LETTER_FORMS = /[ةىؤئ]/g;
/**
 * The canonical spelling of a string for matching purposes — never for
 * display. Lower-cases Latin, folds the Arabic variants above, strips
 * tashkeel/tatweel, turns Arabic-Indic digits into ASCII digits, and trims and
 * collapses whitespace.
 */
export function normalizeArabic(s) {
    return s
        .toLowerCase()
        .replace(STRIPPED, '')
        .replace(ALEF_FORMS, 'ا')
        .replace(SINGLE_LETTER_FORMS, (ch) => SINGLE_LETTERS[ch] ?? ch)
        .replace(ARABIC_DIGITS, (ch) => {
        const code = ch.charCodeAt(0);
        // Both runs are contiguous, so the digit is the offset from the run's start.
        const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
        return String(code - base);
    })
        .replace(/\s+/g, ' ')
        .trim();
}
//# sourceMappingURL=text.js.map